import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_URL = "https://readdy.ai/api/form/db6fe5nqhq9pk5mecte0";

const highlights = [
  { icon: "ri-film-line", title: "影视信息聚合", desc: "收录电影与电视剧的名称、类型、地区、年份、评分与简介等信息。" },
  { icon: "ri-filter-3-line", title: "多维筛选浏览", desc: "支持按题材、地区、年份筛选，并按更新时间、评分与热度排序。" },
  { icon: "ri-search-line", title: "全站快捷检索", desc: "通过关键词快速定位你想了解的影视作品。" },
  { icon: "ri-links-line", title: "同类内容推荐", desc: "基于题材为每部作品提供相关推荐，发现更多好内容。" },
];

interface FeedbackResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function About() {
  usePageMeta(
    "关于我们 - 星辰影视",
    "了解星辰影视的定位、内容范围与联系方式，并通过反馈表单向我们提交内容纠错或建议。",
    "关于星辰影视,影视资讯,反馈,联系方式",
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setFormError("");
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, typeof value === "string" ? value : "");
      });

      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FeedbackResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FeedbackResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <div className="mt-5">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          关于星辰影视
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          星辰影视是一个面向影视爱好者的内容展示平台，聚焦电影与电视剧的分类浏览、详细介绍与热门推荐。我们希望通过清晰的信息组织与便捷的浏览方式，帮助你更快地找到并了解感兴趣的影视作品。
        </p>
      </div>

      <section className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {highlights.map((item) => (
          <div key={item.title} className="bg-background-50 rounded-lg border border-background-200 p-5">
            <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${item.icon} text-xl`} aria-hidden="true" />
            </span>
            <h2 className="mt-3 font-heading font-bold text-base text-foreground-950">{item.title}</h2>
            <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <div className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
          <h2 className="font-heading font-bold text-xl text-foreground-950 flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" aria-hidden="true" />
            内容范围与说明
          </h2>
          <div className="mt-4 space-y-3 text-sm text-foreground-700 leading-relaxed">
            <p>
              本站收录的内容涵盖电影与电视剧两大类别，涉及科幻、悬疑、爱情、动作、剧情、历史、家庭、青春等多种题材，并提供出品地区、上映年份、评分与剧情简介等信息。
            </p>
            <p>
              目前站内展示的影视作品名称、导演、主演、评分与剧情均为演示示例内容，仅用于信息展示与浏览体验，不提供在线播放、下载或任何资源获取服务。
            </p>
            <p>
              我们尊重版权，如内容存在错误或需要调整，欢迎通过右侧的反馈表单与我们联系。
            </p>
          </div>

          <div className="mt-5 pt-5 border-t border-background-200 space-y-2.5 text-sm">
            <div className="flex items-center gap-2 text-foreground-700">
              <i className="ri-mail-line text-primary-600" aria-hidden="true" />
              <span>联系邮箱：contact@xingchen-media.com</span>
            </div>
            <div className="flex items-center gap-2 text-foreground-700">
              <i className="ri-time-line text-primary-600" aria-hidden="true" />
              <span>内容更新时间：每日持续整理</span>
            </div>
            <div className="flex items-center gap-2 text-foreground-700">
              <i className="ri-copyright-line text-primary-600" aria-hidden="true" />
              <span>© 2026 星辰影视 · 保留所有权利</span>
            </div>
          </div>
        </div>

        <div className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
          <h2 className="font-heading font-bold text-xl text-foreground-950 flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-primary-500" aria-hidden="true" />
            意见反馈
          </h2>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
            有任何内容纠错或建议，欢迎告诉我们，我们会认真查看每一条反馈。
          </p>

          {status === "success" ? (
            <div className="mt-5 rounded-lg bg-primary-50 border border-primary-200 p-5 flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-primary-600 text-xl" aria-hidden="true" />
              <div>
                <p className="font-medium text-primary-800">感谢你的反馈！</p>
                <p className="mt-1 text-sm text-primary-700">我们已收到你的留言，会尽快查看处理。</p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-3 text-sm text-primary-700 hover:text-primary-800 underline cursor-pointer"
                >
                  继续提交新的反馈
                </button>
              </div>
            </div>
          ) : (
            <form
              id="xingchen-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 space-y-4"
            >
              <div>
                <label htmlFor="fb-name" className="block text-sm text-foreground-800 mb-1.5">
                  称呼<span className="text-accent-500"> *</span>
                </label>
                <input
                  id="fb-name"
                  name="name"
                  type="text"
                  required
                  maxLength={40}
                  placeholder="请输入你的称呼"
                  className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="fb-email" className="block text-sm text-foreground-800 mb-1.5">
                  邮箱<span className="text-accent-500"> *</span>
                </label>
                <input
                  id="fb-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收我们的回复"
                  className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="fb-type" className="block text-sm text-foreground-800 mb-1.5">
                  反馈类型
                </label>
                <select
                  id="fb-type"
                  name="feedbackType"
                  defaultValue="内容纠错"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
                >
                  <option value="内容纠错">内容纠错</option>
                  <option value="功能建议">功能建议</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="fb-message" className="block text-sm text-foreground-800 mb-1.5">
                  反馈内容<span className="text-accent-500"> *</span>
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请描述你的反馈（不超过 500 字）"
                  className="w-full px-3.5 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-none"
                />
              </div>

              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="comment-verify-node"
              />

              {status === "error" && formError && (
                <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3.5 py-2.5">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full h-11 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
              <p className="text-xs text-foreground-500 text-center">
                我们仅将信息用于回复你的反馈，不会用于其他用途。
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}